import { Component, input, output, model, CUSTOM_ELEMENTS_SCHEMA, computed, signal, inject, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogService } from '../../../core/services/dialog.service';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { QuickPatientDialog } from '../quick-patient-dialog/quick-patient-dialog';
import { EntitySearchDialog } from '../app-search-dialog/app-search-dialog';


@Component({
    selector: 'app-search-picker',
    standalone: true,
    imports: [CommonModule, FormsModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    templateUrl: './app-search-picker.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppSearchPicker),
            multi: true
        }
    ]
})
export class AppSearchPicker implements ControlValueAccessor {
    type = input<'patient' | 'odontologist' | 'business'>('patient');
    label = input<string>('');
    placeholder = input<string>('Seleccionar...');
    icon = input<string>('');

    value = model<any>(null);
    required = input<boolean>(false);
    error = input<string>('');
    classList = input<string>('');
    size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');
    pickerClick = output<void>();
    clearClick = output<void>();
    plusClick = output<void>();
    created = output<any>();
    selected = output<any>();
    private dialogService = inject(DialogService);

    displayValue = computed(() => {
        const val = this.value();
        if (!val) return '';

        if (typeof val === 'string') return val;

        if (this.type() === 'patient') {
            return `${val.firstName || ''} ${val.lastName || ''}`.trim() || val.identification || 'Sin nombre';
        } else if (this.type() === 'odontologist') {
            const user = val.user || val;
            const name = typeof user === 'object' ? user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
            return name ? `Dr. ${name}` : 'Sin nombre';
        } else if (this.type() === 'business') {
            return val.commercialName || val.name || 'Sin nombre';
        }
        return '';
    });

    sizeClasses = computed(() => {
        const map = {
            xs: 'px-2.5 py-1 text-xs min-h-[32px] gap-1',
            sm: 'px-3 py-1.5 text-xs min-h-[36px] gap-1.5',
            md: 'px-4 py-2 text-sm min-h-[40px] gap-2',
            lg: 'px-4 py-3 text-sm min-h-[46px] gap-2',
            xl: 'px-5 py-4 text-base min-h-[52px] gap-2.5'
        };
        return map[this.size()] || map['md'];
    });

    iconSize = computed(() => {
        const map = {
            xs: '14',
            sm: '15',
            md: '17',
            lg: '18',
            xl: '20'
        };
        return map[this.size()] || map['md'];
    });

    // ControlValueAccessor methods
    onChange: any = () => { };
    onTouched: any = () => { };
    isDisabled = signal(false);

    writeValue(val: any): void {
        this.value.set(val);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
    }

    clearValue() {
        this.value.set(null);
        this.onChange(null);
        this.onTouched();
        this.clearClick.emit();
    }

    openSearch() {
        this.pickerClick.emit();

        const modalIcon = this.type() === 'patient' ? 'solar:user-id-bold-duotone' 
                        : this.type() === 'business' ? 'solar:buildings-bold-duotone'
                        : 'solar:users-group-rounded-bold-duotone';

        const title = this.type() === 'patient' ? 'Buscar Paciente' 
                    : this.type() === 'business' ? 'Buscar Negocio / Clínica'
                    : 'Buscar Odontólogo';

        const description = this.type() === 'patient' ? 'Busca por nombre o identificación' 
                          : this.type() === 'business' ? 'Selecciona la clínica para asignar membresía'
                          : 'Selecciona el médico responsable';

        const ref = this.dialogService.open(EntitySearchDialog, {
            title,
            description,
            icon: modalIcon,
            width: '500px',
            data: {
                type: this.type()
            }
        });

        ref.afterClosed().subscribe(res => {
            if (res) {
                if (res.action === 'create_patient') {
                    this.openQuickPatient();
                } else {
                    this.value.set(res);
                    this.onChange(res);
                    this.onTouched();
                    this.selected.emit(res);
                }
            }
        });
    }

    openQuickPatient() {
        const ref = this.dialogService.open(QuickPatientDialog, {
            title: 'Nuevo Paciente',
            description: 'Registra los datos básicos para continuar',
            icon: 'solar:user-id-bold-duotone',
            width: '580px'
        });

        ref.afterClosed().subscribe(res => {
            if (res && res.action === 'create_patient_success' && res.patient) {
                this.selected.emit(res.patient);
                this.value.set(res.patient);
            }
        });
    }
}
